import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, Button, Input, Select, Field } from "@/components/UI";
import { Plus, Baby, Robot, CalendarBlank } from "@phosphor-icons/react";
import { toast } from "sonner";
import { WeeklyCard } from "./WeeklyCard";
import { MonthlyArchive } from "./MonthlyArchive";
import { monthLabel, thisMondayISO, errMsg } from "./helpers";


export function CalfBoard({ mode, stockItems, fmt, onMutate }) {
  const isLely = mode === "lely_calm";
  const slug = isLely ? "lely" : "manual";
  const [cards, setCards] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [saving, setSaving] = useState(false);
  const EMPTY = { stock_id: "", week_start_date: thisMondayISO(), kg_per_calf_per_day: "0.75", total_calf_count: "", notes: "" };
  const [form, setForm] = useState(EMPTY);

  const load = useCallback(() => {
    api.get(`/calf/cards?mode=${mode}`).then(r => setCards(r.data || [])).catch(() => {});
  }, [mode]);
  useEffect(() => { load(); }, [load]);

  const onChanged = (updated) => setCards(prev => prev.map(c => (c.id === updated.id ? updated : c)));
  const onRemoved = (id) => setCards(prev => prev.filter(c => c.id !== id));

  const createCard = async (e) => {
    e.preventDefault();
    if (!form.stock_id) return toast.error("Mama / Ağız Sütü kalemini seçin");
    if (!isLely && (!form.kg_per_calf_per_day || Number(form.kg_per_calf_per_day) <= 0))
      return toast.error("Buzağı başı günlük kg girin");
    setSaving(true);
    try {
      const payload = {
        mode, stock_id: form.stock_id, week_start_date: form.week_start_date, notes: form.notes || null,
      };
      if (isLely) {
        if (form.total_calf_count) payload.total_calf_count = Number(form.total_calf_count);
      } else {
        payload.kg_per_calf_per_day = Number(form.kg_per_calf_per_day);
      }
      await api.post("/calf/cards", payload);
      toast.success("Haftalık çizelge kartı oluşturuldu");
      setForm({ ...EMPTY });
      setShowNew(false);
      load();
    } catch (err) {
      toast.error(errMsg(err, "Kart oluşturulamadı"));
    } finally { setSaving(false); }
  };

  // Aylık gruplama — bulunulan ay ve gelecek açık, geçmiş aylar arşiv kartı
  const currentMonth = new Date().toISOString().slice(0, 7);
  const byMonth = {};
  cards.forEach(c => { const m = c.month || (c.week_start_date || "").slice(0, 7); (byMonth[m] = byMonth[m] || []).push(c); });
  const allMonths = Object.keys(byMonth).sort().reverse();
  const openMonths = allMonths.filter(m => m >= currentMonth);
  const pastMonths = allMonths.filter(m => m < currentMonth);

  const modeTheme = isLely
    ? { Icon: Robot, hint: "Her güne o gün DÖKÜLEN mamayı (kg) girin — girdiğiniz kg anında stoktan düşülür. Haftalık 'Toplam Buzağı Sayısı' ortalama tüketim hesabı içindir." }
    : { Icon: Baby, hint: "Her güne o günkü buzağı sayısını girin (Tab/Enter) — sayı × günlük kg anında stoktan düşülür." };
  const ModeIcon = modeTheme.Icon;

  return (
    <div data-testid={`calf-board-${slug}`}>
      <div className="flex flex-wrap items-center gap-3 mb-3">
        <Button data-testid={`calf-new-${slug}`} onClick={() => setShowNew(v => !v)}>
          <Plus size={14} className="inline mr-1" /> {showNew ? "Kapat" : "Yeni Haftalık Çizelge Kartı"}
        </Button>
        <span className="text-xs text-[#8a938c] flex items-center gap-1.5"><ModeIcon size={14} weight="duotone" /> {modeTheme.hint}</span>
      </div>

      {showNew && (
        <Card className="mb-4" testId={`calf-new-form-${mode}`}>
          <form onSubmit={createCard} className="grid md:grid-cols-4 gap-3">
            <Field label="Mama / Ağız Sütü Kalemi *">
              <Select required data-testid="calf-new-stock" value={form.stock_id} onChange={e => setForm({ ...form, stock_id: e.target.value })}>
                <option value="">Seçin...</option>
                {stockItems.map(s => (
                  <option key={s.id} value={s.id}>{s.name} · {s.category || "—"} ({s.quantity} {s.unit})</option>
                ))}
              </Select>
            </Field>
            <Field label="Hafta Başlangıcı (Pzt) *">
              <Input type="date" required data-testid="calf-new-week" value={form.week_start_date}
                onChange={e => setForm({ ...form, week_start_date: e.target.value })} />
            </Field>
            {isLely ? (
              <Field label="Toplam Buzağı Sayısı">
                <Input type="number" min="0" step="1" data-testid="calf-new-calfcount"
                  value={form.total_calf_count}
                  onChange={e => setForm({ ...form, total_calf_count: e.target.value })}
                  placeholder="Örn: 20 (ortalama için)" />
              </Field>
            ) : (
              <Field label="Buzağı Başı Günlük (kg/gün) *">
                <Input type="number" step="0.01" min="0" required data-testid="calf-new-kgperday"
                  value={form.kg_per_calf_per_day}
                  onChange={e => setForm({ ...form, kg_per_calf_per_day: e.target.value })}
                  placeholder="0.75" />
              </Field>
            )}
            <Field label="Notlar">
              <Input data-testid="calf-new-notes" value={form.notes}
                onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="opsiyonel" />
            </Field>
            <div className="md:col-span-4 flex justify-end gap-2">
              <Button type="button" variant="secondary" onClick={() => setShowNew(false)}>İptal</Button>
              <Button type="submit" disabled={saving} data-testid="calf-new-submit">
                {saving ? "Oluşturuluyor..." : "Kart Oluştur"}
              </Button>
            </div>
          </form>
        </Card>
      )}

      {cards.length === 0 ? (
        <Card testId={`calf-empty-${mode}`}>
          <div className="text-center py-10 text-[#8a938c]">
            <ModeIcon size={34} weight="duotone" className="mx-auto mb-2 opacity-40" />
            Henüz {isLely ? "Lely Calm" : "manuel"} çizelge kartı yok. "Yeni Haftalık Çizelge Kartı" ile başlayın.
          </div>
        </Card>
      ) : (
        <div className="space-y-5">
          {/* Açık aylar (bulunulan + gelecek) */}
          {openMonths.map(m => (
            <div key={m} className="space-y-3">
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-[#8a938c]">
                <CalendarBlank size={14} weight="duotone" /> {monthLabel(m)}
                {m === currentMonth && <span className="text-[10px] font-bold text-[#2D4A36] bg-[#E4EEE6] px-2 py-0.5 rounded-full normal-case tracking-normal">Güncel Ay</span>}
              </div>
              {byMonth[m].map(c => (
                <WeeklyCard key={c.id} card={c} fmt={fmt}
                  onChanged={onChanged} onRemoved={onRemoved} onMutate={onMutate} />
              ))}
            </div>
          ))}

          {/* Arşiv (geçmiş aylar) */}
          {pastMonths.length > 0 && (
            <div className="space-y-3 pt-2">
              <div className="text-xs font-semibold uppercase tracking-wider text-[#8a938c]">Aylık Arşiv</div>
              {pastMonths.map(m => (
                <MonthlyArchive key={m} month={m} cards={byMonth[m]} fmt={fmt}
                  onChanged={onChanged} onRemoved={onRemoved} onMutate={onMutate} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
